import React, { type ReactNode } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { ClickTracking } from '@/components/ClickTracking';
import { PlusIcon } from '@/icons';
import { type ReturnZepIcon } from '@/icons/ZepIcon';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { type QuizListType } from '@/shared/quizList/QuizListType';
import { Button } from '@/ui_v3';
type EmptyQuizListProps = {
  type: QuizListType;
  title?: string;
  description?: string;
  goToActionInfo?: {
    href: string;
    icon: ReturnZepIcon;
    label: string;
    target: '_blank' | '_open';
  };
  /**
   * 기본 CTA(링크 이동) 대신 쓸 액션.
   *
   * 기본 CTA 는 `<Link>` 라 라우팅만 가능한데, 모달을 여는 목록(예: 폴더)도 있다.
   * 넘기면 `goToActionInfo` 와 기본 트래킹을 건너뛰고 이 노드를 그대로 그린다
   * (트래킹은 넘기는 쪽이 자기 이벤트로 감싼다).
   *
   * ⚠️ **액션을 아예 없애려면 `null` 을 넘긴다.** 생략(`undefined`)은 "기본 CTA 를 쓰겠다"는
   * 뜻이라 `퀴즈 만들기` 가 나온다 — 남의 폴더처럼 만들기가 말이 안 되는 자리에서
   * 조건부로 `undefined` 를 넘겼다가 엉뚱한 버튼이 뜬 적이 있다.
   */
  actionSlot?: ReactNode;
  /**
   * 기본 일러스트 대신 쓸 이미지.
   *
   * 빈 상태 골격(간격·타이포·액션 자리)은 목록마다 같지만 그림은 다를 수 있다 —
   * 폴더 상세는 전용 일러스트를 쓴다(Figma 34281:41405).
   */
  imageSlot?: ReactNode;
};
export const EmptyQuizList = (props: EmptyQuizListProps) => {
  const {
    searchText
  } = useSearchQuizQuery();
  const {
    t
  } = useTranslation(['common']);
  const goToActionInfo: EmptyQuizListProps['goToActionInfo'] = props.goToActionInfo ?? {
    target: '_blank',
    href: '/build',
    icon: PlusIcon,
    label: t('common.makeQuiz')
  };
  const GotoActionIcon = goToActionInfo.icon;
  const emptyText = searchText ? t('quizList.empty.bySearch') : t('quizList.empty.byFilter');
  return <div className={'flex flex-1 flex-col items-start justify-center py-2xl'} data-sentry-component="EmptyQuizList" data-sentry-source-file="EmptyQuizList.tsx">
      <div className={'flex w-full flex-col items-center justify-center gap-md'}>
        <div className={'flex flex-col items-center justify-center gap-xs'}>
          {props.imageSlot ?? <img className={'aspect-[3/2] h-[128px] w-[192px] shrink-0'} src="/assets/empty_avatar.webp" alt={'mark'} />}
          {searchText && <span className={'text-heading-md-bold text-default'}>
              {'"' + searchText + '"'}
            </span>}
          <span className={'text-heading-md-bold text-alternative'}>
            {props.title ? props.title : emptyText}
          </span>
          <span className={'text-body-lg-medium text-assistive'}>
            {props.description ? props.description : t('quizList.empty.suggestion')}
          </span>
        </div>
        {/* `??` 가 아니다 — `null`(액션 없음)과 생략(기본 CTA)을 갈라야 한다. */}
        {props.actionSlot !== undefined ? props.actionSlot : <ClickTracking eventName={props.type === 'myQuiz' ? 'create_quiz_empty_myquiz' : 'create_quiz_empty_public'}>
            <Button intent={'alternative'} variant={'outlined'} size={'lg'} rounded={'full'} asChild={true}>
              <Link href={goToActionInfo.href} target={goToActionInfo.target} prefetch={false}>
                <GotoActionIcon size={'md'} />
                {goToActionInfo.label}
              </Link>
            </Button>
          </ClickTracking>}
      </div>
    </div>;
};